import { useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { GUEST_SESSION, LIBRARY } from "../design/copy.js";
import { CaseKindRow } from "./case-card.js";
import { DRAG_Z } from "./case-lightbox.js";
import type { AuthMode } from "../routes.js";
import { Lettering } from "./primitives.js";

/**
 * WHAT A GUEST SESSION MEANS (2026-09-24, Andre): the paper the header's
 * "Guest session." opens. It is the case lightbox's paper — opaque, the kind
 * row in its bar with "put it down." across from it, a Museo title, the body
 * on the sans, the doors along a rule at the foot — laid in the middle of a
 * dimmed page, the way the start-over question is, because it is a real
 * modal: it takes focus, keeps Tab inside it, and Escape, the backdrop or
 * "put it down." close it (the header then hands focus back to its button).
 *
 * Portalled to the body: the header is a sticky stacking context, and a
 * backdrop drawn inside it would sit under the desk's own lightboxes.
 */
export function GuestSessionLightbox({ onClose, onDoor }: { onClose: () => void; onDoor: (mode: AuthMode) => void }): ReactNode {
  const paperRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    paperRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  /**
   * Escape from inside the paper is this paper's alone: stopped here, so a
   * case lightbox open on the desk behind it stays open. aria-modal promises
   * the page behind is out of reach; Tab has to agree.
   */
  const onPaperKey = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== "Tab" || paperRef.current === null) return;
    const doors = Array.from(paperRef.current.querySelectorAll<HTMLButtonElement>("button:not([disabled])"));
    const first = doors[0];
    const last = doors.at(-1);
    if (first === undefined || last === undefined) return;
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === paperRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: DRAG_Z + 3, background: "color-mix(in srgb, var(--pc-ink) 28%, transparent)" }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        ref={paperRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pc-guest-session-title"
        aria-describedby="pc-guest-session-body"
        tabIndex={-1}
        onKeyDown={onPaperKey}
        className="pc-case-lightbox pc-lightbox-in pc-guest-lightbox w-full max-w-[34rem] outline-none"
      >
        <header className="pc-case-lightbox-bar" style={{ cursor: "default" }}>
          <div className="pc-case-lightbox-kind">
            <CaseKindRow words={GUEST_SESSION.kind} glyph="plan" />
          </div>
          <button type="button" onClick={onClose} aria-label="Put this down">
            <Lettering text={LIBRARY.putDown} />
          </button>
        </header>
        <div className="pc-case-lightbox-body">
          <h2 id="pc-guest-session-title" className="pc-case-lightbox-title">
            {GUEST_SESSION.title}
          </h2>
          <div id="pc-guest-session-body">
            {GUEST_SESSION.body.map((line) => (
              <p key={line} className="pc-guest-lightbox-text">
                {line}
              </p>
            ))}
            <p className="pc-case-lightbox-question">{GUEST_SESSION.staysBehind}</p>
          </div>
          <div className="pc-case-lightbox-doors pc-case-doors">
            <button type="button" onClick={() => onDoor("sign-in")}>
              <Lettering text={GUEST_SESSION.logIn} />
            </button>
            <button type="button" onClick={() => onDoor("sign-up")}>
              <Lettering text={GUEST_SESSION.createAccount} />
            </button>
          </div>
        </div>
      </section>
    </div>,
    document.body,
  );
}
